import type {UseQueryOptions, UseSuspenseQueryOptions} from '@tanstack/react-query'
import {keepPreviousData, queryOptions, skipToken, useSuspenseQuery} from '@tanstack/react-query'

import type {SupportedLocales} from '@/lib/locale/locale'
import type {Product, RawProduct} from '@/models/productModel'
import {getProduct} from '@/services/productService'
import {NO_REFETCH_OPTIONS} from '@/utils/query/constants'

export function getProductQueryKey(params: {
  retailer: RawProduct['retailer']
  id: RawProduct['id']
  locale: SupportedLocales
}) {
  return ['product', params.retailer, params.id, params.locale] as const
}

export function getProductQueryOptions<TData = Product | false, TError = Error>(
  params: {retailer: RawProduct['retailer']; id: RawProduct['id']; locale: SupportedLocales},
  options?: Omit<UseQueryOptions<Product | false, TError, TData>, 'queryKey'>,
) {
  return queryOptions({
    queryKey: getProductQueryKey(params),
    queryFn: async () =>
      getProduct({retailer: params.retailer, id: params.id, locale: params.locale}),
    placeholderData: keepPreviousData,
    staleTime: 15 * 60 * 100,
    ...NO_REFETCH_OPTIONS,
    ...options,
  })
}

export function useProductSuspense<TData = Product | false, TError = Error>(
  params: {retailer: RawProduct['retailer']; id: RawProduct['id']; locale: SupportedLocales},
  options?: Omit<UseQueryOptions<Product | false, TError, TData>, 'queryKey' | 'queryFn'>,
) {
  const opts = getProductQueryOptions<TData, TError>(params, options) as UseSuspenseQueryOptions<
    Product | false,
    TError,
    TData
  >
  return useSuspenseQuery(opts)
}

// Observe-only helper for product (no fetch)
export function getProductObserveQueryOptions<TData = unknown, TError = Error>(
  params: {retailer: RawProduct['retailer']; id: RawProduct['id']; locale: SupportedLocales},
  options?: Omit<UseQueryOptions<Product | false, TError, TData>, 'queryKey' | 'queryFn'>,
) {
  return getProductQueryOptions(params, {
    queryFn: skipToken,
    enabled: false,
    ...options,
  })
}
